// ===========================================================================
// ActiveFilterChips — fila horizontal de filtros activos como chips removibles.
//
// Cada chip tiene una × que elimina ese filtro específico. "Clear all" al final
// remueve todos. El chip de Time usa el color de severidad del bucket. La fila
// desaparece si no hay filtros activos.
// ===========================================================================
import React from "react";
import { Box, Chip, Button, Stack } from "@mui/material";
import { TIME_BUCKET_BADGE_COLORS } from "../../common/constants";
import { formatDate } from "../../utils/format";

// Orden y etiqueta legible de cada filtro. `locationName` resuelve el id → nombre.
const buildChips = (filters, locationName) => {
  const chips = [];
  if (filters.search) chips.push({ key: "search", label: `Search: ${filters.search}` });
  if (filters.networkName) chips.push({ key: "networkName", label: `Network: ${filters.networkName}` });
  if (filters.timeBucket)
    chips.push({ key: "timeBucket", label: `Time: ${filters.timeBucket}`, bucket: filters.timeBucket });
  if (filters.metalLevel) chips.push({ key: "metalLevel", label: `Metal: ${filters.metalLevel}` });
  if (filters.pcpName) chips.push({ key: "pcpName", label: `PCP: ${filters.pcpName}` });
  if (filters.locationId)
    chips.push({ key: "locationId", label: `Location: ${locationName?.(filters.locationId) || filters.locationId}` });
  if (filters.dob) chips.push({ key: "dob", label: `DOB: ${formatDate(filters.dob)}` });
  return chips;
};

export default function ActiveFilterChips({ filters, locationName, onRemove, onClearAll }) {
  const chips = buildChips(filters, locationName);
  if (chips.length === 0) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 2, overflowX: "auto" }}
    >
      {chips.map((c) => {
        const sev = c.bucket ? TIME_BUCKET_BADGE_COLORS[c.bucket] : null;
        return (
          <Chip
            key={c.key}
            label={c.label}
            size="small"
            onDelete={() => onRemove(c.key)}
            aria-label={`Remove filter ${c.label}`}
            sx={
              sev
                ? { bgcolor: sev.bg, color: sev.text, fontWeight: 600, "& .MuiChip-deleteIcon": { color: sev.text } }
                : undefined
            }
            variant={sev ? "filled" : "outlined"}
          />
        );
      })}
      <Box sx={{ flex: 1 }} />
      <Button size="small" color="inherit" onClick={onClearAll} sx={{ textTransform: "none" }}>
        Clear all
      </Button>
    </Stack>
  );
}
